import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import AppAnalytics from '../analytics/AppAnalytics';

@customElement('header-nav')
export class HeaderNav extends LitElement {
  @state()
  private toolsOpen = false;

  @state()
  private mobileMenuOpen = false;

  @state()
  private mobileToolsOpen = false;

  static styles = css`
    :host {
      display: block;
    }

    .header {
      background: linear-gradient(135deg,
        rgba(0, 212, 255, 0.1) 0%,
        rgba(168, 85, 247, 0.1) 50%,
        rgba(236, 72, 153, 0.1) 100%);
      backdrop-filter: blur(12px);
      border-bottom: 1px solid rgba(0, 212, 255, 0.3);
      position: fixed;
      width: 100%;
      z-index: 50;
      top: 0;
    }

    .nav {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 1rem;
      max-width: 1200px;
      margin: 0 auto;
    }

    .logo {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      text-decoration: none;
      group: hover;
    }

    .logo-emoji {
      font-size: 1.875rem;
      animation: float 6s ease-in-out infinite;
    }

    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-10px); }
    }

    .logo-text {
      font-size: 1.5rem;
      font-weight: bold;
      font-family: 'JetBrains Mono', monospace;
      color: #00d4ff;
      text-shadow: 0 0 5px currentColor, 0 0 10px currentColor, 0 0 15px currentColor;
      transition: color 0.2s;
    }

    .logo:hover .logo-text {
      color: #7dd3fc;
    }

    .desktop-nav {
      display: none;
      align-items: center;
      gap: 2rem;
    }

    @media (min-width: 768px) {
      .desktop-nav {
        display: flex;
      }
    }

    .nav-link {
      color: rgba(226, 232, 240, 0.8);
      text-decoration: none;
      font-family: 'JetBrains Mono', monospace;
      font-weight: 500;
      letter-spacing: 0.05em;
      transition: all 0.2s;
    }

    .nav-link:hover {
      color: #00d4ff;
      text-shadow: 0 0 5px currentColor, 0 0 10px currentColor, 0 0 15px currentColor;
    }

    .tools-dropdown {
      position: relative;
    }

    .tools-button {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      background: none;
      border: none;
      color: rgba(226, 232, 240, 0.8);
      font-family: 'JetBrains Mono', monospace;
      font-weight: 500;
      letter-spacing: 0.05em;
      cursor: pointer;
      transition: all 0.2s;
    }

    .tools-button:hover {
      color: #00d4ff;
      text-shadow: 0 0 5px currentColor, 0 0 10px currentColor, 0 0 15px currentColor;
    }

    .tools-icon {
      width: 1rem;
      height: 1rem;
      transition: transform 0.2s;
      color: #00d4ff;
    }

    .tools-icon.open {
      transform: rotate(180deg);
    }

    .dropdown-menu {
      position: absolute;
      right: 0;
      top: 100%;
      margin-top: 0.5rem;
      width: 14rem;
      background: linear-gradient(135deg,
        rgba(0, 212, 255, 0.1) 0%,
        rgba(168, 85, 247, 0.1) 50%,
        rgba(236, 72, 153, 0.1) 100%);
      backdrop-filter: blur(10px);
      border: 1px solid rgba(0, 212, 255, 0.3);
      border-radius: 0.5rem;
      padding: 0.5rem 0;
      opacity: 0;
      visibility: hidden;
      transform: scale(0.95);
      transition: all 0.1s;
    }

    .dropdown-menu.open {
      opacity: 1;
      visibility: visible;
      transform: scale(1);
    }

    .dropdown-link {
      display: block;
      padding: 0.75rem 1rem;
      color: rgba(226, 232, 240, 0.9);
      text-decoration: none;
      font-family: 'JetBrains Mono', monospace;
      font-size: 0.875rem;
      transition: all 0.2s;
    }

    .dropdown-link:hover {
      color: #00d4ff;
      background: rgba(0, 212, 255, 0.1);
    }

    .mobile-menu-button {
      display: block;
      background: none;
      border: 2px solid transparent;
      background: linear-gradient(135deg, rgba(0, 212, 255, 0.1), rgba(168, 85, 247, 0.1)) padding-box,
                  linear-gradient(135deg, #00d4ff, #a855f7) border-box;
      color: #00d4ff;
      padding: 0.5rem;
      border-radius: 0.5rem;
      cursor: pointer;
    }

    @media (min-width: 768px) {
      .mobile-menu-button {
        display: none;
      }
    }

    .mobile-menu {
      position: fixed;
      top: 0;
      right: 0;
      height: 100vh;
      width: 18rem;
      max-width: calc(100vw - 2rem);
      background: linear-gradient(135deg,
        rgba(0, 212, 255, 0.1) 0%,
        rgba(168, 85, 247, 0.1) 50%,
        rgba(236, 72, 153, 0.1) 100%);
      backdrop-filter: blur(10px);
      border-left: 1px solid rgba(0, 212, 255, 0.3);
      padding: 1.5rem;
      display: flex;
      flex-direction: column;
      transform: translateX(100%);
      transition: transform 0.2s;
      z-index: 60;
    }

    .mobile-menu.open {
      transform: translateX(0);
    }

    .mobile-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(5, 10, 20, 0.8);
      backdrop-filter: blur(4px);
      z-index: 50;
      opacity: 0;
      visibility: hidden;
      transition: all 0.2s;
    }

    .mobile-backdrop.open {
      opacity: 1;
      visibility: visible;
    }

    .mobile-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 2rem;
    }

    .mobile-title {
      font-size: 1.25rem;
      font-weight: bold;
      font-family: 'JetBrains Mono', monospace;
      color: #00d4ff;
      text-shadow: 0 0 5px currentColor, 0 0 10px currentColor, 0 0 15px currentColor;
    }

    .mobile-close {
      background: none;
      border: 2px solid transparent;
      background: linear-gradient(135deg, rgba(0, 212, 255, 0.1), rgba(168, 85, 247, 0.1)) padding-box,
                  linear-gradient(135deg, #00d4ff, #a855f7) border-box;
      color: #00d4ff;
      padding: 0.5rem;
      border-radius: 0.5rem;
      cursor: pointer;
    }

    .mobile-nav {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;
    }

    .mobile-nav-link {
      color: rgba(226, 232, 240, 0.8);
      text-decoration: none;
      font-family: 'JetBrains Mono', monospace;
      letter-spacing: 0.05em;
      transition: color 0.2s;
    }

    .mobile-nav-link:hover {
      color: #00d4ff;
    }

    .mobile-tools {
      display: flex;
      flex-direction: column;
    }

    .mobile-tools-button {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      background: none;
      border: none;
      color: rgba(226, 232, 240, 0.8);
      font-family: 'JetBrains Mono', monospace;
      letter-spacing: 0.05em;
      cursor: pointer;
      transition: color 0.2s;
      text-align: left;
    }

    .mobile-tools-button:hover {
      color: #00d4ff;
    }

    .mobile-tools-menu {
      padding-left: 1rem;
      margin-top: 0.75rem;
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      max-height: 0;
      overflow: hidden;
      transition: max-height 0.2s;
    }

    .mobile-tools-menu.open {
      max-height: 200px;
    }

    .mobile-tools-link {
      color: rgba(226, 232, 240, 0.7);
      text-decoration: none;
      font-family: 'JetBrains Mono', monospace;
      font-size: 0.875rem;
      transition: color 0.2s;
    }

    .mobile-tools-link:hover {
      color: #00d4ff;
    }

    @media (max-width: 767px) {
      .mobile-menu {
        display: flex;
      }
    }

    @media (min-width: 768px) {
      .mobile-menu,
      .mobile-backdrop {
        display: none;
      }
    }
  `;

  private trackNavigation(link: string, location: string) {
    AppAnalytics.getPlausible()?.trackEvent('navigation_clicked', {
      props: { link, location }
    });
  }

  private toggleToolsDropdown() {
    this.toolsOpen = !this.toolsOpen;
  }

  private closeToolsDropdown() {
    this.toolsOpen = false;
  }

  private toggleMobileMenu() {
    this.mobileMenuOpen = !this.mobileMenuOpen;
    if (!this.mobileMenuOpen) {
      this.mobileToolsOpen = false;
    }
  }

  private closeMobileMenu() {
    this.mobileMenuOpen = false;
    this.mobileToolsOpen = false;
  }

  private toggleMobileTools() {
    this.mobileToolsOpen = !this.mobileToolsOpen;
  }

  private handleBackdropClick() {
    this.closeMobileMenu();
  }

  // Close dropdown when clicking outside
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.handleDocumentClick.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.handleDocumentClick.bind(this));
  }

  private handleDocumentClick(e: Event) {
    if (!this.contains(e.target as Node)) {
      this.closeToolsDropdown();
    }
  }

  render() {
    return html`
      <header class="header">
        <nav class="nav">
          <a href="/" class="logo" @click=${() => this.trackNavigation('home', 'logo')}>
            <div class="logo-emoji">🌸</div>
            <span class="logo-text">DIFFLORA</span>
          </a>

          <div class="desktop-nav">
            <a
              href="/"
              class="nav-link"
              @click=${() => this.trackNavigation('home', 'header')}>
              HOME
            </a>
            <a
              href="/about"
              class="nav-link"
              @click=${() => this.trackNavigation('about', 'header')}>
              ABOUT
            </a>

            <a
              href="/blog"
              class="nav-link"
              @click=${() => this.trackNavigation('blog', 'header')}>
              BLOG
            </a>
            <a
              href="/contact"
              class="nav-link"
              @click=${() => this.trackNavigation('contact', 'header')}>
              CONTACT
            </a>
            <a
              href="https://diffloraverse.com"
              class="nav-link"
              target="_blank"
              rel="noopener noreferrer"
              @click=${() => this.trackNavigation('post_model', 'header')}>
              POST A MODEL
            </a>
          </div>

          <button class="mobile-menu-button" @click=${this.toggleMobileMenu}>
            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16m-16 6h16"/>
            </svg>
          </button>
        </nav>
      </header>

      <!-- Mobile Backdrop -->
      <div
        class="mobile-backdrop ${this.mobileMenuOpen ? 'open' : ''}"
        @click=${this.handleBackdropClick}>
      </div>

      <!-- Mobile Menu -->
      <div class="mobile-menu ${this.mobileMenuOpen ? 'open' : ''}">
        <div class="mobile-header">
          <span class="mobile-title">MENU_</span>
          <button class="mobile-close" @click=${this.closeMobileMenu}>
            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div class="mobile-nav">
          <a
            href="/"
            class="mobile-nav-link"
            @click=${() => this.trackNavigation('home', 'mobile_menu')}>
            HOME
          </a>
          <a
            href="/about"
            class="mobile-nav-link"
            @click=${() => this.trackNavigation('about', 'mobile_menu')}>
            ABOUT
          </a>

          <a
            href="/blog"
            class="mobile-nav-link"
            @click=${() => this.trackNavigation('blog', 'mobile_menu')}>
            BLOG
          </a>
          <a
            href="/contact"
            class="mobile-nav-link"
            @click=${() => this.trackNavigation('contact', 'mobile_menu')}>
            CONTACT
          </a>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'header-nav': HeaderNav;
  }
}
